/**
 * AdminPortalAccess (Item 4) — Super Admin control panel for portal-wide
 * access: an instant maintenance lockdown and an optional global business-
 * hours schedule (IST). When either blocks access, EVERYONE except the Super
 * Admin is locked out (including Company Admins), even mid-session.
 */
import React, { useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';
import { Lock, Clock, ShieldAlert, Save, RotateCcw, LogOut, Paperclip, Scale, Tag, Plus, Trash2, Loader2 } from 'lucide-react';
import {
  WeeklyScheduleEditor, DEFAULT_BUSINESS_SCHEDULE, EMPTY_SCHEDULE, normalizeSchedule,
} from '@/components/admin/WeeklyScheduleEditor';

// Wave 3.99ad — Ready-made lockdown banner messages the Super Admin can pick
// from (they can still type a custom one). "Neutral" is the default privacy-
// first "page not found" style; the others are explicit maintenance notices.
const LOCKDOWN_MESSAGE_PRESETS = [
  {
    key: 'neutral',
    label: 'Neutral (page not found)',
    text: "The page you're looking for isn't available. It may have been moved or no longer exists.",
  },
  {
    key: 'maintenance',
    label: 'Scheduled maintenance',
    text: 'The portal is temporarily down for scheduled maintenance. Please check back shortly.',
  },
  {
    key: 'back-soon',
    label: 'Back soon',
    text: 'We’ll be right back. The portal is undergoing maintenance — please try again in a little while.',
  },
];

export default function AdminPortalAccess() {
  const { isSuperAdmin } = useAuth();
  if (!isSuperAdmin) return <Navigate to="/dashboard" replace />;
  return (
    <AppShell title="Portal Access" subtitle="Lock the portal for maintenance or restrict everyone (except you) to business hours.">
      <PortalAccessContent />
    </AppShell>
  );
}

function PortalAccessContent() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lockdown, setLockdown] = useState(false);
  const [message, setMessage] = useState('');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [schedule, setSchedule] = useState(EMPTY_SCHEDULE);
  const [serverIst, setServerIst] = useState('');
  // Idle auto-logout policy (global).
  const [idleEnabled, setIdleEnabled] = useState(true);
  const [idleMinutes, setIdleMinutes] = useState(30);
  const [idleWarnSeconds, setIdleWarnSeconds] = useState(60);
  // Per-module attachment size caps (KB). Managed independently of the portal
  // settings above via /system/attachment-caps.
  const [caps, setCaps] = useState({ erp_kb: 500, notes_kb: 500, tasks_kb: 500 });
  const [savingCaps, setSavingCaps] = useState(false);
  // Ghat weight tolerance — a small over-weight margin (grams and/or % of the
  // gold issued, whichever is larger) allowed before a Ghat tick is blocked.
  const [ghatTolGrams, setGhatTolGrams] = useState(1.5);
  const [ghatTolPercent, setGhatTolPercent] = useState(0);
  const [savingGhatTol, setSavingGhatTol] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/system/portal-access');
      setLockdown(!!data.portal_lockdown);
      setMessage(data.portal_lockdown_message || '');
      setScheduleEnabled(!!data.portal_schedule_enabled);
      setSchedule(normalizeSchedule(data.portal_schedule));
      setIdleEnabled(data.idle_logout_enabled !== false);
      setIdleMinutes(Number(data.idle_logout_minutes || 30));
      setIdleWarnSeconds(Number(data.idle_logout_warning_seconds || 60));
      setGhatTolGrams(Number(data.ghat_tolerance_grams ?? 1.5));
      setGhatTolPercent(Number(data.ghat_tolerance_percent ?? 0));
      try {
        const st = await api.get('/system/portal-status');
        setServerIst(st.data?.server_time_ist || '');
      } catch { /* noop */ }
      try {
        const capsRes = await api.get('/system/attachment-caps');
        setCaps({
          erp_kb: Number(capsRes.data?.erp_kb || 500),
          notes_kb: Number(capsRes.data?.notes_kb || 500),
          tasks_kb: Number(capsRes.data?.tasks_kb || 500),
        });
      } catch { /* noop */ }
    } catch (e) {
      toast.error(extractError(e, 'Failed to load portal settings'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/system/portal-access', {
        portal_lockdown: lockdown,
        portal_lockdown_message: message,
        portal_schedule_enabled: scheduleEnabled,
        portal_schedule: schedule,
        idle_logout_enabled: idleEnabled,
        idle_logout_minutes: Number(idleMinutes) || 30,
        idle_logout_warning_seconds: Number(idleWarnSeconds) || 60,
      });
      setSchedule(normalizeSchedule(data.portal_schedule));
      if (data.idle_logout_minutes) setIdleMinutes(Number(data.idle_logout_minutes));
      if (data.idle_logout_warning_seconds) setIdleWarnSeconds(Number(data.idle_logout_warning_seconds));
      toast.success('Portal access settings saved');
    } catch (e) {
      toast.error(extractError(e, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-16 text-center text-sm text-slate-500" data-testid="portal-access-loading">Loading…</div>;
  }

  const saveCaps = async () => {
    setSavingCaps(true);
    try {
      const { data } = await api.put('/system/attachment-caps', {
        erp_kb: Number(caps.erp_kb) || 500,
        notes_kb: Number(caps.notes_kb) || 500,
        tasks_kb: Number(caps.tasks_kb) || 500,
      });
      setCaps({
        erp_kb: Number(data?.erp_kb || 500),
        notes_kb: Number(data?.notes_kb || 500),
        tasks_kb: Number(data?.tasks_kb || 500),
      });
      toast.success('Attachment size caps saved');
    } catch (e) {
      toast.error(extractError(e, 'Failed to save attachment caps'));
    } finally {
      setSavingCaps(false);
    }
  };

  const saveGhatTolerance = async () => {
    setSavingGhatTol(true);
    try {
      const { data } = await api.put('/system/portal-access', {
        ghat_tolerance_grams: Number(ghatTolGrams) || 0,
        ghat_tolerance_percent: Number(ghatTolPercent) || 0,
      });
      setGhatTolGrams(Number(data?.ghat_tolerance_grams ?? 1.5));
      setGhatTolPercent(Number(data?.ghat_tolerance_percent ?? 0));
      toast.success('Ghat tolerance saved');
    } catch (e) {
      toast.error(extractError(e, 'Failed to save Ghat tolerance'));
    } finally {
      setSavingGhatTol(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-5">
      {/* Maintenance lockdown */}
      <Card className="p-5" data-testid="portal-lockdown-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${lockdown ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 text-slate-500'}`}>
              <Lock size={18} />
            </div>
            <div>
              <div className="font-medium text-[var(--ink-navy)] flex items-center gap-2">
                Maintenance Lockdown
                {lockdown
                  ? <Badge className="bg-rose-50 text-rose-700" data-testid="portal-lockdown-badge-on">ON</Badge>
                  : <Badge className="bg-emerald-50 text-emerald-700" data-testid="portal-lockdown-badge-off">OFF</Badge>}
              </div>
              <p className="text-xs text-slate-500 mt-1 max-w-lg">
                When ON, only you (Super Admin) can access the portal. Everyone else — including
                Company Admins — is locked out immediately, even if already logged in.
              </p>
            </div>
          </div>
          <Switch checked={lockdown} onCheckedChange={setLockdown} data-testid="portal-lockdown-switch" />
        </div>
        <div className="mt-4">
          <Label className="text-xs">Message shown to locked-out users (optional)</Label>
          {/* Wave 3.99ad — Quick-fill presets so the Super Admin can SELECT a
              ready-made banner or WRITE their own below. This exact text is
              what a locked-out user sees on the login screen. */}
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid="portal-lockdown-presets">
            <span className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mr-0.5">Quick fill</span>
            {LOCKDOWN_MESSAGE_PRESETS.map((p) => {
              const active = (message || '').trim() === p.text.trim();
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setMessage(p.text)}
                  className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                    active
                      ? 'border-[var(--ink-navy)] bg-[var(--ink-navy)] text-white'
                      : 'border-[var(--divider)] bg-white text-slate-600 hover:bg-slate-50 hover:text-[var(--ink-navy)]'
                  }`}
                  title={p.text}
                  data-testid={`portal-lockdown-preset-${p.key}`}
                >
                  {p.label}
                </button>
              );
            })}
            {message && (
              <button
                type="button"
                onClick={() => setMessage('')}
                className="inline-flex items-center rounded-full border border-transparent px-2 py-1 text-[11px] text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                data-testid="portal-lockdown-message-clear"
              >
                Clear
              </button>
            )}
          </div>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="The page you're looking for isn't available. It may have been moved or no longer exists."
            className="mt-2"
            rows={3}
            data-testid="portal-lockdown-message"
          />
          <p className="text-[10px] text-slate-500 mt-1">
            Pick a preset above or write your own. This is the exact banner shown on the sign-in screen after users are locked out.
            Leave blank to use the default neutral message.
          </p>
        </div>
        {lockdown && (
          <div className="mt-3 flex items-start gap-2 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700" data-testid="portal-lockdown-warning">
            <ShieldAlert size={14} className="mt-0.5 shrink-0" />
            <span>Lockdown is active once saved. Non-super-admin users cannot log in or view any data until you turn it off.</span>
          </div>
        )}
      </Card>

      {/* Global business hours */}
      <Card className="p-5" data-testid="portal-hours-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${scheduleEnabled ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-500'}`}>
              <Clock size={18} />
            </div>
            <div>
              <div className="font-medium text-[var(--ink-navy)]">Global Business Hours (IST)</div>
              <p className="text-xs text-slate-500 mt-1 max-w-lg">
                When enabled, everyone except you is allowed in only during these weekly windows.
                Outside them the portal behaves as locked for all other users.
                {serverIst && <span className="block mt-1">Server time now: <span className="font-mono text-slate-600">{serverIst} IST</span></span>}
              </p>
            </div>
          </div>
          <Switch checked={scheduleEnabled} onCheckedChange={setScheduleEnabled} data-testid="portal-hours-switch" />
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-end gap-2 mb-2">
            <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={() => setSchedule(DEFAULT_BUSINESS_SCHEDULE)} data-testid="portal-hours-default">
              <RotateCcw size={12} className="mr-1" /> Default hours
            </Button>
            <Button type="button" variant="ghost" size="sm" className="h-8 text-xs text-slate-500" onClick={() => setSchedule(EMPTY_SCHEDULE)} data-testid="portal-hours-clear">
              Clear all
            </Button>
          </div>
          <WeeklyScheduleEditor value={schedule} onChange={setSchedule} disabled={!scheduleEnabled} testId="portal-hours-editor" />
        </div>
      </Card>

      {/* Idle auto-logout */}
      <Card className="p-5" data-testid="portal-idle-logout-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${idleEnabled ? 'bg-[var(--diamond-bg)] text-[var(--diamond-icon)]' : 'bg-slate-100 text-slate-500'}`}>
              <LogOut size={18} />
            </div>
            <div>
              <div className="font-medium text-[var(--ink-navy)]">Idle Auto-Logout</div>
              <p className="text-xs text-slate-500 mt-1 max-w-lg">
                Automatically sign users out after a period of inactivity. A short warning
                countdown appears first so they can stay signed in. Applies to everyone
                except Super Admins.
              </p>
            </div>
          </div>
          <Switch checked={idleEnabled} onCheckedChange={setIdleEnabled} data-testid="portal-idle-switch" />
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
          <div>
            <Label htmlFor="idle-minutes">Log out after (minutes)</Label>
            <Input
              id="idle-minutes"
              type="number"
              min={1}
              max={1440}
              value={idleMinutes}
              onChange={(e) => setIdleMinutes(e.target.value)}
              disabled={!idleEnabled}
              className="mt-1"
              data-testid="portal-idle-minutes-input"
            />
          </div>
          <div>
            <Label htmlFor="idle-warn">Warning countdown (seconds)</Label>
            <Input
              id="idle-warn"
              type="number"
              min={10}
              max={600}
              value={idleWarnSeconds}
              onChange={(e) => setIdleWarnSeconds(e.target.value)}
              disabled={!idleEnabled}
              className="mt-1"
              data-testid="portal-idle-warning-input"
            />
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">Default: 30 minutes idle · 60-second warning. Range: 1–1440 min, 10–600 sec.</p>
      </Card>

      {/* Attachment size caps (per module) */}
      <Card className="p-5" data-testid="attachment-caps-card">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--diamond-bg)] text-[var(--diamond-icon)]">
            <Paperclip size={18} />
          </div>
          <div>
            <div className="font-medium text-[var(--ink-navy)]">Attachment Size Caps</div>
            <p className="text-xs text-slate-500 mt-1 max-w-lg">
              Maximum upload size per file, set separately for each module. Images larger
              than the cap are auto-compressed to fit; PDFs above it are blocked. Notes &amp;
              Tasks caps apply to every project. Enter the size in kilobytes (KB).
            </p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
          <div>
            <Label htmlFor="cap-erp">ERP (Order files)</Label>
            <div className="mt-1 flex items-center gap-1.5">
              <Input id="cap-erp" type="number" min={50} max={51200} value={caps.erp_kb}
                onChange={(e) => setCaps((c) => ({ ...c, erp_kb: e.target.value }))}
                className="w-full" data-testid="attachment-cap-erp-input" />
              <span className="text-xs text-slate-500">KB</span>
            </div>
          </div>
          <div>
            <Label htmlFor="cap-notes">Notes</Label>
            <div className="mt-1 flex items-center gap-1.5">
              <Input id="cap-notes" type="number" min={50} max={51200} value={caps.notes_kb}
                onChange={(e) => setCaps((c) => ({ ...c, notes_kb: e.target.value }))}
                className="w-full" data-testid="attachment-cap-notes-input" />
              <span className="text-xs text-slate-500">KB</span>
            </div>
          </div>
          <div>
            <Label htmlFor="cap-tasks">Tasks</Label>
            <div className="mt-1 flex items-center gap-1.5">
              <Input id="cap-tasks" type="number" min={50} max={51200} value={caps.tasks_kb}
                onChange={(e) => setCaps((c) => ({ ...c, tasks_kb: e.target.value }))}
                className="w-full" data-testid="attachment-cap-tasks-input" />
              <span className="text-xs text-slate-500">KB</span>
            </div>
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">Default: 500 KB each. Range: 50 KB – 51200 KB (50 MB).</p>
        <div className="mt-3 flex justify-end">
          <Button onClick={saveCaps} disabled={savingCaps} variant="outline" size="sm" className="h-8 text-xs" data-testid="attachment-caps-save">
            <Save size={13} className="mr-1.5" /> {savingCaps ? 'Saving…' : 'Save Caps'}
          </Button>
        </div>
      </Card>

      {/* Ghat weight tolerance */}
      <Card className="p-5" data-testid="ghat-tolerance-card">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--diamond-bg)] text-[var(--diamond-icon)]">
            <Scale size={18} />
          </div>
          <div>
            <div className="font-medium text-[var(--ink-navy)]">Ghat Weight Tolerance</div>
            <p className="text-xs text-slate-500 mt-1 max-w-lg">
              A small over-weight margin the Ghat (base setting) weight may exceed the
              gold issued by, to absorb casting &amp; scale variance before blocking a Ghat
              confirmation. The effective allowance is the <span className="font-medium">larger</span> of
              the fixed grams and the percentage of the gold issued.
            </p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
          <div>
            <Label htmlFor="ghat-tol-grams">Fixed margin (grams)</Label>
            <div className="mt-1 flex items-center gap-1.5">
              <Input
                id="ghat-tol-grams"
                type="number"
                step="0.001"
                min={0}
                max={50}
                value={ghatTolGrams}
                onChange={(e) => setGhatTolGrams(e.target.value)}
                className="w-full"
                data-testid="ghat-tolerance-grams-input"
              />
              <span className="text-xs text-slate-500">g</span>
            </div>
          </div>
          <div>
            <Label htmlFor="ghat-tol-percent">Percentage of issued</Label>
            <div className="mt-1 flex items-center gap-1.5">
              <Input
                id="ghat-tol-percent"
                type="number"
                step="0.1"
                min={0}
                max={25}
                value={ghatTolPercent}
                onChange={(e) => setGhatTolPercent(e.target.value)}
                className="w-full"
                data-testid="ghat-tolerance-percent-input"
              />
              <span className="text-xs text-slate-500">%</span>
            </div>
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">Default: 1.500 g fixed · 0% . Range: 0–50 g, 0–25%. The larger of the two applies.</p>
        <div className="mt-3 flex justify-end">
          <Button onClick={saveGhatTolerance} disabled={savingGhatTol} variant="outline" size="sm" className="h-8 text-xs" data-testid="ghat-tolerance-save">
            <Save size={13} className="mr-1.5" /> {savingGhatTol ? 'Saving…' : 'Save Tolerance'}
          </Button>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="luxury-button" data-testid="portal-access-save">
          <Save size={14} className="mr-1.5" /> {saving ? 'Saving…' : 'Save Settings'}
        </Button>
      </div>

      {/* Brand Names (per-company) */}
      <BrandNamesCard />
    </div>
  );
}


// ===========================================================================
// Brand Names — per-company master configured by the Super Admin. Each
// company has its own list (one or more). These feed the mandatory Brand
// dropdown on the final step of the Receive-Jewellery wizard.
// ===========================================================================
function BrandNamesCard() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newBrand, setNewBrand] = useState('');
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState('');

  // Load companies once.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/companies/');
        const list = Array.isArray(data) ? data : [];
        setCompanies(list);
        if (list.length && !companyId) setCompanyId(list[0].id);
      } catch (e) {
        toast.error(extractError(e, 'Failed to load companies'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBrands = async (cid) => {
    if (!cid) return;
    setLoading(true);
    try {
      const { data } = await api.get('/brands', { params: { company_id: cid } });
      setBrands(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load brands'));
      setBrands([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) loadBrands(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const addBrand = async () => {
    const name = (newBrand || '').trim();
    if (!name) { toast.error('Enter a brand name'); return; }
    if (!companyId) { toast.error('Select a company first'); return; }
    setAdding(true);
    try {
      await api.post('/brands', { company_id: companyId, name });
      setNewBrand('');
      await loadBrands(companyId);
      toast.success('Brand added');
    } catch (e) {
      toast.error(extractError(e, 'Failed to add brand'));
    } finally {
      setAdding(false);
    }
  };

  const toggleActive = async (b) => {
    setBusyId(b.id);
    try {
      await api.put(`/brands/${b.id}`, { active: !(b.active !== false) });
      await loadBrands(companyId);
    } catch (e) {
      toast.error(extractError(e, 'Failed to update brand'));
    } finally {
      setBusyId('');
    }
  };

  const deleteBrand = async (b) => {
    setBusyId(b.id);
    try {
      await api.delete(`/brands/${b.id}`);
      await loadBrands(companyId);
      toast.success(`Brand "${b.name}" deleted`);
    } catch (e) {
      toast.error(extractError(e, 'Failed to delete brand'));
    } finally {
      setBusyId('');
    }
  };

  const companyName = (companies.find((c) => c.id === companyId) || {}).name || '';

  return (
    <Card className="p-5" data-testid="brand-names-card">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--diamond-bg)] text-[var(--diamond-icon)]">
          <Tag size={18} />
        </div>
        <div>
          <div className="font-medium text-[var(--ink-navy)]">Brand Names</div>
          <p className="text-xs text-slate-500 mt-1 max-w-lg">
            Configure the brands available per company. These appear in the mandatory
            <span className="font-medium"> Brand</span> dropdown on the final step of
            Receive Jewellery — a piece cannot be received without one. Disable a brand to
            stop it appearing on new receipts while keeping it on existing pieces.
          </p>
        </div>
      </div>

      <div className="mt-4 max-w-xl space-y-4">
        {/* Company selector */}
        <div>
          <Label className="text-xs">Company</Label>
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger className="mt-1 h-10" data-testid="brand-company-select">
              <SelectValue placeholder="Select a company…" />
            </SelectTrigger>
            <SelectContent>
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`brand-company-option-${c.id}`}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Add new brand */}
        <div>
          <Label className="text-xs">Add a brand{companyName ? ` to ${companyName}` : ''}</Label>
          <div className="mt-1 flex items-center gap-2">
            <Input
              value={newBrand}
              onChange={(e) => setNewBrand(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addBrand(); } }}
              placeholder="e.g. Aurelia"
              className="h-10"
              disabled={!companyId || adding}
              data-testid="brand-new-input"
            />
            <Button
              onClick={addBrand}
              disabled={!companyId || adding || !newBrand.trim()}
              className="h-10 shrink-0"
              data-testid="brand-add-button"
            >
              {adding ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Plus size={14} className="mr-1.5" />}
              Add
            </Button>
          </div>
        </div>

        {/* Brand list */}
        <div data-testid="brand-list">
          {loading ? (
            <div className="text-sm text-slate-500 py-4" data-testid="brand-list-loading">Loading brands…</div>
          ) : brands.length === 0 ? (
            <div className="text-sm text-slate-500 rounded-md border border-dashed border-slate-200 py-6 text-center" data-testid="brand-list-empty">
              No brands yet for this company. Add the first one above.
            </div>
          ) : (
            <div className="rounded-lg border border-[var(--divider)] divide-y divide-[var(--divider)]">
              {brands.map((b) => {
                const active = b.active !== false;
                return (
                  <div key={b.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`brand-row-${b.id}`}>
                    <Tag size={14} className={active ? 'text-emerald-600' : 'text-slate-400'} />
                    <span className={`text-sm font-medium ${active ? 'text-[var(--ink-navy)]' : 'text-slate-400 line-through'}`} data-testid={`brand-name-${b.id}`}>
                      {b.name}
                    </span>
                    {active
                      ? <Badge className="bg-emerald-50 text-emerald-700" data-testid={`brand-active-${b.id}`}>Active</Badge>
                      : <Badge className="bg-slate-100 text-slate-500" data-testid={`brand-inactive-${b.id}`}>Disabled</Badge>}
                    <div className="ml-auto flex items-center gap-2">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[11px] text-slate-500">Active</span>
                        <Switch
                          checked={active}
                          onCheckedChange={() => toggleActive(b)}
                          disabled={busyId === b.id}
                          data-testid={`brand-toggle-${b.id}`}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                        onClick={() => deleteBrand(b)}
                        disabled={busyId === b.id}
                        aria-label={`Delete ${b.name}`}
                        title="Delete brand"
                        data-testid={`brand-delete-${b.id}`}
                      >
                        {busyId === b.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
